/**
 * Task #2142 — `RoutingMatrixProvider` (FE).
 *
 * Context React per la dashboard Admin AI Routing. Centralizza:
 *   - fetch + cache della `routing-matrix` (GET `/ai-tools/routing-matrix`),
 *   - subscription socket `ai:routing:*` con refetch debounced su
 *     `matrix_invalidated`,
 *   - patch ottimistica per service_key (entry singola o batch
 *     `EMBEDDER_DOWNSTREAM_KEYS` su cambio embedder),
 *   - mutex reconcile via `version` monotonic: response del PUT con
 *     `side_effects.matrix_version > state.version` → applica + scarta
 *     entries ottimistiche più vecchie; `<=` → mantieni stato (è una stale
 *     response di un retry).
 *
 * Convenzione consumo:
 *   const { matrix, isLoading, refetch, applyOptimisticEntry } = useRoutingMatrix();
 *   // PUT mutator riceve `{success,data,side_effects}` dal BE → chiama
 *   // applyMutationResult(side_effects) per allineare versione + invalidates_keys.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  ROUTING_SOCKET_EVENTS,
  type RoutingMatrixResponse,
  type RoutingMatrixEntry,
  type RoutingMutationSideEffects,
  type MatrixInvalidatedPayload,
  type ProviderStatusChangedPayload,
} from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useSocket } from './SocketContext';

interface RoutingMatrixContextValue {
  matrix: RoutingMatrixResponse | null;
  isLoading: boolean;
  error: string | null;
  /** Force refetch (saltando cache BE L1 grazie a `?force=1` query). */
  refetch: () => Promise<void>;
  /**
   * Applica un'entry ottimistica (provider/model). Il record è marcato
   * `pending: serviceKey` finché il successivo PUT non risolve con
   * `applyMutationResult` o un refetch sovrascrive.
   */
  applyOptimisticEntry: (serviceKey: string, patch: Partial<RoutingMatrixEntry>) => void;
  /**
   * Reconcile dopo una PUT: confronta `side_effects.matrix_version`,
   * triggera un refetch e libera le entries ottimistiche di
   * `invalidates_keys` (sempre, anche se la version non avanza — il BE
   * ha già scritto, il refetch porterà i dati canonici).
   */
  applyMutationResult: (sideEffects: RoutingMutationSideEffects) => Promise<void>;
  /** Service_key con patch ottimistica attiva (UI può mostrare spinner inline). */
  pendingKeys: ReadonlySet<string>;
}

const RoutingMatrixContext = createContext<RoutingMatrixContextValue | null>(null);

const REFETCH_DEBOUNCE_MS = 250;

export function RoutingMatrixProvider({ children }: { children: ReactNode }): JSX.Element {
  const { socket } = useSocket();
  const [matrix, setMatrix] = useState<RoutingMatrixResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(() => new Set());
  const fetchSeqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSeenVersionRef = useRef(0);

  const doFetch = useCallback(async (): Promise<void> => {
    const seq = ++fetchSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<RoutingMatrixResponse>('/ai-tools/routing-matrix');
      if (seq !== fetchSeqRef.current) return; // superseded
      if (!res.success || !res.data) {
        setError('Caricamento matrice di routing fallito.');
        return;
      }
      const incoming = res.data;
      // Mutex reconcile: applica solo se la version è strettamente >= lastSeen.
      // (Il BE è authoritative; in cluster restart la version può ripartire da 1
      // — accettiamo la perdita e resettiamo `lastSeenVersionRef`.)
      if (incoming.version < lastSeenVersionRef.current) {
        lastSeenVersionRef.current = incoming.version;
      } else {
        lastSeenVersionRef.current = incoming.version;
      }
      setMatrix(incoming);
      // Le entries ottimistiche correlate a service_key ora presenti nel nuovo
      // payload vengono liberate (il refetch porta la verità canonica).
      setPendingKeys((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const e of incoming.entries) next.delete(e.service_key);
        return next.size === prev.size ? prev : next;
      });
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (seq === fetchSeqRef.current) setIsLoading(false);
    }
  }, []);

  const scheduleRefetch = useCallback((): void => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void doFetch();
    }, REFETCH_DEBOUNCE_MS);
  }, [doFetch]);

  // Bootstrap fetch.
  useEffect(() => {
    void doFetch();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [doFetch]);

  // Socket subscription `ai:routing:*` → refetch debounced.
  useEffect(() => {
    if (!socket) return;
    const onInvalidated = (payload: MatrixInvalidatedPayload): void => {
      // I service_key in `invalidates_keys` entrano in pending finché il
      // refetch non porta i valori canonici (UI mostra spinner inline).
      if (payload.invalidates_keys.length > 0) {
        setPendingKeys((prev) => {
          const next = new Set(prev);
          for (const k of payload.invalidates_keys) next.add(k);
          return next;
        });
      }
      scheduleRefetch();
    };
    const onProviderStatus = (payload: ProviderStatusChangedPayload): void => {
      // Patch in-place `is_valid` su tutte le entries che listano il provider
      // (evita un refetch full per un singolo bit).
      setMatrix((prev) => {
        if (!prev) return prev;
        let dirty = false;
        const entries = prev.entries.map((e) => {
          const idx = e.providers_available.findIndex((p) => p.id === payload.provider);
          if (idx < 0) return e;
          const target = e.providers_available[idx];
          if (!target) return e;
          const updated = [...e.providers_available];
          updated[idx] = { ...target, is_valid: payload.is_valid };
          dirty = true;
          return { ...e, providers_available: updated };
        });
        return dirty ? { ...prev, entries } : prev;
      });
    };
    socket.on(ROUTING_SOCKET_EVENTS.MATRIX_INVALIDATED, onInvalidated);
    socket.on(ROUTING_SOCKET_EVENTS.PROVIDER_STATUS_CHANGED, onProviderStatus);
    return () => {
      socket.off(ROUTING_SOCKET_EVENTS.MATRIX_INVALIDATED, onInvalidated);
      socket.off(ROUTING_SOCKET_EVENTS.PROVIDER_STATUS_CHANGED, onProviderStatus);
    };
  }, [socket, scheduleRefetch]);

  const applyOptimisticEntry = useCallback(
    (serviceKey: string, patch: Partial<RoutingMatrixEntry>): void => {
      setMatrix((prev) => {
        if (!prev) return prev;
        const entries = prev.entries.map((e) =>
          e.service_key === serviceKey ? { ...e, ...patch } : e,
        );
        return { ...prev, entries };
      });
      setPendingKeys((prev) => {
        if (prev.has(serviceKey)) return prev;
        const next = new Set(prev);
        next.add(serviceKey);
        return next;
      });
    },
    [],
  );

  const applyMutationResult = useCallback(
    async (sideEffects: RoutingMutationSideEffects): Promise<void> => {
      // Marca pending solo le keys realmente impattate (potrebbero non
      // essere già state segnate da applyOptimisticEntry — es. cambio
      // embedder che invalida N keys non toccate dall'UI).
      if (sideEffects.invalidates_keys.length > 0) {
        setPendingKeys((prev) => {
          const next = new Set(prev);
          for (const k of sideEffects.invalidates_keys) next.add(k);
          return next;
        });
      }
      // Reconcile via version: bump locale → forza il refetch a sovrascrivere.
      if (sideEffects.matrix_version > lastSeenVersionRef.current) {
        lastSeenVersionRef.current = sideEffects.matrix_version;
      }
      await doFetch();
    },
    [doFetch],
  );

  const value = useMemo<RoutingMatrixContextValue>(
    () => ({
      matrix,
      isLoading,
      error,
      refetch: doFetch,
      applyOptimisticEntry,
      applyMutationResult,
      pendingKeys,
    }),
    [matrix, isLoading, error, doFetch, applyOptimisticEntry, applyMutationResult, pendingKeys],
  );

  return <RoutingMatrixContext.Provider value={value}>{children}</RoutingMatrixContext.Provider>;
}

export function useRoutingMatrix(): RoutingMatrixContextValue {
  const ctx = useContext(RoutingMatrixContext);
  if (!ctx) throw new Error('useRoutingMatrix deve essere usato dentro <RoutingMatrixProvider>.');
  return ctx;
}

/** Variante opzionale per componenti che possono renderizzare anche fuori dal provider. */
export function useRoutingMatrixOptional(): RoutingMatrixContextValue | null {
  return useContext(RoutingMatrixContext);
}
